import { create } from 'zustand';
import {
  TOKEN_KEY,
  USER_KEY,
  clearStoredSession,
  getRememberedRefreshToken,
  getStoredToken,
  getStoredUser,
  isTokenValid,
} from '../utils/token';

/* Sessions survive reloads: the token + user live in localStorage and auth state
   is hydrated from them on startup.
   - Expired JWT on a remembered device (within its 30 days) → still signed in;
     the API client refreshes the token before the first request.
   - Expired JWT otherwise → discard everything, start signed out (→ login page).
   - Valid/opaque token → signed in (server-side expiry is caught by the 401
     interceptor during use).
   - No token but a stored user → the backend session is cookie-based; stay
     signed in and let the API 401 if the cookie is gone. */
const storedToken = getStoredToken();
let hydratedAuthed = false;
if (storedToken) {
  if (isTokenValid(storedToken) || getRememberedRefreshToken()) {
    hydratedAuthed = true;
  } else {
    clearStoredSession();
  }
} else if (getStoredUser()) {
  hydratedAuthed = true;
}

/* login_step "3" means the account finished onboarding. Anything else lands on
   the sign-up wizard instead of the portal. A user object we can't read at all
   (cookie-only session) is treated as complete — the API decides from there. */
export const PROFILE_COMPLETE_STEP = 3;
export const isProfileComplete = (user) =>
  !user || user.login_step == null || Number(user.login_step) === PROFILE_COMPLETE_STEP;

const useAuthStore = create((set) => ({
  isAuthenticated: hydratedAuthed,
  user: hydratedAuthed ? getStoredUser() : null,
  phone: '',
  /* E.164 form sent to the passwordless API, e.g. "+15125550147". */
  username: '',
  /* When the last OTP went out (first send or resend) — the code page locks
     Resend for a cooldown from this moment. In-memory only, like phone/username:
     a reload sends the person back to the login page anyway. */
  otpSentAt: 0,

  setLoginIdentity: (phone, username) => set({ phone, username }),
  markOtpSent: () => set({ otpSentAt: Date.now() }),

  login: (token, user = null) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ isAuthenticated: true, user });
  },

  /* Signing out also forgets a remembered device. */
  logout: () => {
    clearStoredSession();
    set({ isAuthenticated: false, user: null });
  },
}));

export default useAuthStore;
